import * as React from 'react';
import { FormControl, InputLabel, makeStyles, MenuItem, Select, Tooltip } from '@material-ui/core';
import { Error } from '@material-ui/icons';
import { useSegmentSharingUpdate } from '../../../hooks/useSegmentUpdate';
import { SegmentOverviewResult } from '../../../models/segments';
import { fontFamilies } from '../../../theme/text';
import { neutral, status } from '../../../theme/colors';

interface Props {
  segment: SegmentOverviewResult;
}

const useStyles = makeStyles({
  select: {
    minWidth: 105,
    // visually hide the select label
    '& label.Mui-focused, & label.MuiFormLabel-filled': {
      clip: 'rect(0 0 0 0)',
      clipPath: 'inset(50%)',
      height: 1,
      overflow: 'hidden',
      position: 'absolute',
      whiteSpace: 'nowrap',
      width: 1
    },
    '& .MuiSelect-root': {
      padding: '5px 26px 5px 6px',
      fontSize: 12,
      fontWeight: 300,
      fontFamily: fontFamilies.body,
      borderColor: neutral[300]
    },
    '& .MuiSelect-iconOutlined': {
      right: 0
    }
  },
  innerContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  errorIcon: {
    marginLeft: 4
  }
});

const SharingCell = ({ segment }: Props) => {
  const overrides = useStyles();
  const segmentSharingMutation = useSegmentSharingUpdate();
  const userInterface = window.env.USER_INTERFACE != null ? window.env.USER_INTERFACE : 'default';
  const [isShared, setIsShared] = React.useState<boolean>(segment.isShared);

  React.useEffect(() => {
    setIsShared(segment.isShared);
  }, [segment]);

  function onSharedChange(shared: 'Share' | 'Do not share') {
    segmentSharingMutation.mutate(
      { segmentId: segment.id, isShared: shared === 'Share' },
      { onSuccess: (_data, variables) => setIsShared(variables.isShared) }
    );
  }

  if (userInterface === 'management' || !segment.isEditable) {
    return <td>{segment.isShared ? 'Shared' : 'Not Shared'}</td>;
  }

  return (
    <td>
      <div className={overrides.innerContainer}>
        {
          <FormControl size="small" variant="outlined" className={overrides.select}>
            <InputLabel id="shared-select-label">Shared</InputLabel>
            <Select
              labelId="shared-select-label"
              id="shared-select"
              value={isShared ? 'Share' : 'Do not share'}
              onChange={event => onSharedChange(event.target.value as 'Share' | 'Do not share')}
              disabled={segmentSharingMutation.isLoading}
            >
              <MenuItem value="Share">Share</MenuItem>
              <MenuItem value="Do not share">Do not share</MenuItem>
            </Select>
          </FormControl>
        }
        {segmentSharingMutation.isError && (
          <Tooltip title={segmentSharingMutation.error.message}>
            <Error htmlColor={status.error.dark} fontSize="small" className={overrides.errorIcon} />
          </Tooltip>
        )}
      </div>
    </td>
  );
};

export default SharingCell;
